import { useMutation, useQuery } from '@tanstack/react-query';
import {
    getCustomerByPcode,
    createCustomerForPcode,
    getInvoicesByPcode,
    getSubscriptionByPcode,
    getCardsByPcode,
    cancelSubscription,
    createSubscription,
    removeCard,
    updateCustomerBillingDetails,
    downloadInvoice,
} from '../api/payment';
import type {
    Cards,
    ChargebeeCustomer,
    Invoice,
    Subscription,
} from '../utils/types';

const CUSTOMER_QUERY = 'customer';
const SUBSCRIPTION_QUERY = 'subscription';
const CARDS_QUERY = 'cards';
const INVOICES_QUERY = 'invoices';

export const useCustomer = (pCode: string) => {
    return useQuery<ChargebeeCustomer | null>({
        queryKey: [CUSTOMER_QUERY, pCode],
        queryFn: () => getCustomerByPcode(pCode),
        enabled: !!pCode,
        // Don't retry on 404 (which now returns null, not an error)
        retry: (failureCount, error) => {
            // Don't retry if it's a "not found" error that somehow still gets thrown
            if (error instanceof Error && error.message.includes('not found')) {
                return false;
            }
            return failureCount < 3;
        },
    });
};

export const useCreateCustomer = () => {
    return useMutation({
        mutationFn: ({ pCode, data }: { pCode: string; data: any }) =>
            createCustomerForPcode(pCode, data),
    });
};

export const useSubscription = (pCode: string) => {
    return useQuery<Subscription | null>({
        queryKey: [SUBSCRIPTION_QUERY, pCode],
        queryFn: () => getSubscriptionByPcode(pCode),
        enabled: !!pCode,
    });
};

export const useCreateSubscription = () => {
    return useMutation({
        mutationFn: (siteId: string) => createSubscription(siteId),
    });
};

export const useCancelSubscription = () => {
    return useMutation({
        mutationFn: ({ siteId, reason }: { siteId: string; reason?: string }) => cancelSubscription(siteId, reason),
    });
};

export const useCards = (pCode: string) => {
    return useQuery<Cards[]>({
        queryKey: [CARDS_QUERY, pCode],
        queryFn: () => getCardsByPcode(pCode),
        enabled: !!pCode,
    });
};

export const useInvoices = (pCode: string) => {
    return useQuery<Invoice[]>({
        queryKey: [INVOICES_QUERY, pCode],
        queryFn: () => getInvoicesByPcode(pCode),
        enabled: !!pCode,
    });
};

export const useRemoveCard = () => {
    return useMutation({
        mutationFn: ({ pcode, cardId }: { pcode: string; cardId: string }) => removeCard(pcode, cardId),
    });
};

export const useUpdateCustomerBillingDetails = () => {
    return useMutation({
        mutationFn: ({ pcode, data }: { pcode: string; data: any }) => updateCustomerBillingDetails(pcode, data),
    });
};

export const useDownloadInvoice = (onSuccess: (data: any) => void) => {
    return useMutation({
        mutationFn: ({ invoiceId }: { invoiceId: string }) => downloadInvoice(invoiceId),
        onSuccess: (data: any) => onSuccess(data),
    });
};